<div class="layout-editor">

  <!-- ── Empty state ── -->
  @if ((resolvedMembers$ | async)?.length === 0) {
    <div class="empty-state">
      <nb-icon icon="layout-outline" pack="eva"></nb-icon>
      <span>Noch keine Felder. Klicke auf „Feld hinzufügen".</span>
    </div>
  }

  <!-- ── Layout canvas ── -->
  <div class="layout-canvas"
       [sortableArray]="value"
       [sortableOptions]="sortableOptions"
       (sortableChange)="onSort()">

    @for (member of (resolvedMembers$ | async) ?? []; track member.fieldId) {
      <div class="field-block"
           [style.grid-column]="'span ' + member.colSpan"
           [class.disabled]="isDisabled">

        <!-- Main row: drag handle + name/chips, always gets full width -->
        <div class="field-main">
          <nb-icon class="drag-handle" icon="more-vertical-outline" pack="eva"
                   nbTooltip="Ziehen zum Verschieben" nbTooltipPlacement="left"></nb-icon>

          <div class="field-content">
            <div class="field-header">
              <span class="field-name">{{ member.field.name }}</span>
              @if (member.required && isDisabled) {
                <span class="required-chip" nbTooltip="Pflichtfeld">
                  <nb-icon icon="checkmark-circle-2" pack="eva"></nb-icon>
                  Pflicht
                </span>
              }
              @if (member.rule; as rule) {
                <span class="visibility-chip" [class.stale]="rule.stale"
                      [nbTooltip]="rule.stale
                        ? 'Der hinterlegte Wert existiert nicht mehr – Regel bitte neu setzen'
                        : 'Wird nur angezeigt, wenn die Bedingung erfüllt ist'">
                  <nb-icon icon="eye-outline" pack="eva"></nb-icon>
                  Nur wenn {{ rule.triggerName }} = {{ rule.valueLabel }}
                </span>
              }
              <span class="type-chip">{{ displayFieldType(member.field.composition.valueType) }}</span>
              @if (member.field.composition.unit) {
                <span class="unit-chip">
                  {{ displayUnit(member.field.composition.unit) }}
                </span>
              }
            </div>
            <div class="field-width-hint">{{ widthPercent(member.colSpan ?? 12) }}</div>
          </div>
        </div>

        <!-- Controls row: wraps onto its own line, never squeezes the name above -->
        @if (!isDisabled) {
          <div class="field-controls">
            <button type="button" nbButton ghost size="tiny" class="required-toggle"
                    [class.active]="member.required"
                    [nbTooltip]="member.required ? 'Pflichtfeld – klicken zum Entfernen' : 'Als Pflichtfeld markieren – gilt überall, wo diese Feldgruppe verwendet wird'"
                    (click)="toggleRequired(member.fieldId)">
              <nb-icon [icon]="member.required ? 'checkmark-circle-2' : 'checkmark-circle-2-outline'" pack="eva"></nb-icon>
              Pflicht
            </button>

            <button type="button" nbButton ghost size="tiny" class="visibility-toggle"
                    [class.active]="member.visibleIfFieldId"
                    nbTooltip="Sichtbarkeit an ein anderes Feld koppeln"
                    [nbPopover]="visibilityRuleTemplate"
                    [nbPopoverContext]="member"
                    [nbPopoverPlacement]="NbPosition.BOTTOM_START"
                    [nbPopoverAdjustment]="NbAdjustment.COUNTERCLOCKWISE"
                    nbPopoverTrigger="noop"
                    #visibilityPopover="nbPopover"
                    (click)="toggleVisibilityPopover($event, visibilityPopover)">
              <nb-icon [icon]="member.visibleIfFieldId ? 'eye' : 'eye-outline'" pack="eva"></nb-icon>
              Sichtbarkeit
            </button>

            <div class="span-selector">
              @for (opt of colSpanOptions; track opt.value) {
                <button type="button" nbButton ghost size="tiny"
                        [class.active]="member.colSpan === opt.value"
                        [nbTooltip]="opt.tooltip"
                        (click)="setColSpan(member.fieldId, opt.value)">
                  {{ opt.label }}
                </button>
              }
            </div>

            <button type="button" nbButton ghost size="tiny" class="remove-btn"
                    nbTooltip="Entfernen" (click)="remove(member.fieldId)">
              <nb-icon icon="close-outline" pack="eva"></nb-icon>
            </button>
          </div>
        }
      </div>
    }

  </div>

  @if (resolvedMembers$ | async; as members) {
    @if (hasRequiredMember(members)) {
      <div class="required-scope-hint">
        <nb-icon icon="info-outline" pack="eva"></nb-icon>
        Pflichtfelder gelten überall, wo diese Feldgruppe verwendet wird (Protokolle und TGA-Typen).
      </div>
    }
    @if (hasVisibilityRule(members)) {
      <div class="required-scope-hint">
        <nb-icon icon="info-outline" pack="eva"></nb-icon>
        Sichtbarkeitsregeln gelten überall, wo diese Feldgruppe verwendet wird. Ausgeblendete Felder
        werden nicht abgefragt und erscheinen nicht im PDF.
      </div>
    }
  }

  <!-- ── Add button ── -->
  @if (!isDisabled) {
    <div class="add-row">
      <button nbButton ghost size="small" type="button"
              [nbPopover]="fieldsPickerTemplate"
              [nbPopoverPlacement]="NbPosition.BOTTOM_START"
              [nbPopoverAdjustment]="NbAdjustment.COUNTERCLOCKWISE"
              #fieldsPicker="nbPopover">
        <nb-icon icon="plus-outline" pack="eva"></nb-icon>
        Feld hinzufügen
      </button>
    </div>
  }

</div>

<!-- ── Picker popover ── -->
<ng-template #fieldsPickerTemplate>
  <div class="fields-picker">

    <div class="picker-search">
      <nb-form-field>
        <nb-icon nbPrefix icon="search-outline" pack="eva"></nb-icon>
        <input nbInput fullWidth size="small" placeholder="Suchen..."
               [value]="searchText$.value"
               (input)="onSearchInput($event)"
               autocomplete="off">
      </nb-form-field>
    </div>

    @if (currentFolder$ | async; as currentFolder) {
      <div class="picker-breadcrumb">
        <button nbButton ghost size="tiny" type="button" (click)="navigateToParent($event)">
          <nb-icon icon="arrow-back-outline" pack="eva"></nb-icon>
          {{ folderDisplayName(currentFolder) }}
        </button>
      </div>
    }

    @if (((searchText$ | async) ?? '') === '') {
      @if (foldersWithCount$ | async; as folders) {
        @if (folders.length) {
          <div class="picker-folders">
            @for (folder of folders; track folder.fullPath) {
              <div class="picker-folder-item"
                   role="button" tabindex="0"
                   (click)="navigateToFolder($event, folder.fullPath)"
                   (keydown.enter)="navigateToFolder($event, folder.fullPath)">
                <nb-icon icon="folder-outline" pack="eva"></nb-icon>
                <span class="folder-name">{{ folder.name }}</span>
                <span class="folder-count">{{ folder.count }}</span>
                <nb-icon icon="chevron-right-outline" pack="eva" class="folder-arrow"></nb-icon>
              </div>
            }
          </div>
        }
      }
    }

    <div class="picker-fields">
      @if (filteredFields$ | async; as fields) {
        @if (!fields.length) {
          <div class="picker-empty">
            <nb-icon icon="inbox-outline" pack="eva"></nb-icon>
            <span>Keine Felder gefunden</span>
          </div>
        }
        @for (field of fields; track field.id) {
          <div class="picker-field-item"
               role="button" tabindex="0"
               (click)="selectField(field)"
               (keydown.enter)="selectField(field)">
            <div class="picker-field-body">
              <div class="picker-field-header">
                <span class="picker-field-name">{{ field.name }}</span>
                @if ((searchText$ | async) && field.category) {
                  <span class="picker-field-category">
                    {{ categoryLabel(field.category) }}
                  </span>
                }
              </div>
              <div class="picker-field-meta">
                <span class="picker-type-chip">{{ displayFieldType(field.composition.valueType) }}</span>
                @if (field.composition.unit) {
                  <span class="picker-unit-chip">
                    {{ displayUnit(field.composition.unit) }}
                  </span>
                }
              </div>
            </div>
          </div>
        }
      }
    </div>

  </div>
</ng-template>

<!-- ── Visibility rule popover ──
     Nebular wraps nbPopoverContext as { $implicit: context }, so the member arrives through the
     IMPLICIT binding (let-member), not a named one. A named binding silently yields undefined. -->
<ng-template #visibilityRuleTemplate let-member>
  <div class="visibility-rule">

    @if (member.candidates.length === 0) {
      <div class="visibility-empty">
        Diese Feldgruppe enthält kein geeignetes Auslöserfeld. Geeignet sind Felder vom Typ Ja/Nein
        oder Multiple Choice mit einem einzelnen Wert.
      </div>

      @if (member.visibleIfFieldId) {
        <button type="button" nbButton ghost size="tiny" class="visibility-clear"
                (click)="clearVisibilityRule(member.fieldId)">
          <nb-icon icon="close-outline" pack="eva"></nb-icon>
          Regel entfernen
        </button>
      }
    } @else {
      <div class="visibility-row">
        <span class="visibility-label">Nur anzeigen wenn</span>
        <nb-select size="small" placeholder="Feld wählen"
                   [selected]="member.visibleIfFieldId"
                   (selectedChange)="setVisibilityTrigger(member, $event)">
          @for (candidate of member.candidates; track candidate.fieldId) {
            <nb-option [value]="candidate.fieldId">{{ candidate.name }}</nb-option>
          }
        </nb-select>
      </div>

      @if (member.visibleIfFieldId) {
        <div class="visibility-row">
          <span class="visibility-label">ist gleich</span>
          <nb-select size="small" placeholder="Wert wählen"
                     [selected]="member.visibleIfValue"
                     (selectedChange)="setVisibilityValue(member.fieldId, $event)">
            @for (option of member.triggerOptions; track option.value) {
              <nb-option [value]="option.value">{{ option.label }}</nb-option>
            }
          </nb-select>
        </div>

        <button type="button" nbButton ghost size="tiny" class="visibility-clear"
                (click)="clearVisibilityRule(member.fieldId)">
          <nb-icon icon="close-outline" pack="eva"></nb-icon>
          Regel entfernen
        </button>
      }
    }

  </div>
</ng-template>
